import { create } from 'zustand';
import { type AiWorkbenchAction } from '@/api/ai';
import { resolveSurface } from '@/screens/registry';
import { type PanelDescriptor } from '@/screens/types';
import { BASE_GRID_COLS, getWorkspaceGridCols, scaleLayoutCols } from './grid';

function generatePanelId(): string {
  return `panel-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function maxBottom(panels: PanelDescriptor[]): number {
  return panels.reduce((acc, panel) => Math.max(acc, panel.layout.y + panel.layout.h), 0);
}

function workspacePanels(panels: PanelDescriptor[]): PanelDescriptor[] {
  return panels.filter((panel) => !panel.detached);
}

function sanitizeLayout(
  layout: { x: number; y: number; w: number; h: number },
  cols = BASE_GRID_COLS,
): { x: number; y: number; w: number; h: number } {
  const width = Math.max(1, Math.min(cols, Math.floor(layout.w)));
  const height = Math.max(1, Math.floor(layout.h));
  const x = Math.max(0, Math.min(cols - width, Math.floor(layout.x)));
  const y = Math.max(0, Math.floor(layout.y));
  return { x, y, w: width, h: height };
}

function intersects(
  left: { x: number; y: number; w: number; h: number },
  right: { x: number; y: number; w: number; h: number },
): boolean {
  return (
    left.x < right.x + right.w &&
    left.x + left.w > right.x &&
    left.y < right.y + right.h &&
    left.y + left.h > right.y
  );
}

function findFirstFitPosition(
  panels: PanelDescriptor[],
  nextSize: { w: number; h: number },
  cols: number,
): { x: number; y: number } {
  const visiblePanels = workspacePanels(panels);
  const existing = visiblePanels.map((panel) => sanitizeLayout(panel.layout, cols));
  const maxY = Math.max(maxBottom(visiblePanels), 0);

  for (let y = 0; y <= maxY; y += 1) {
    for (let x = 0; x <= cols - nextSize.w; x += 1) {
      const candidate = { x, y, w: nextSize.w, h: nextSize.h };
      const blocked = existing.some((slot) => intersects(slot, candidate));
      if (!blocked) {
        return { x, y };
      }
    }
  }

  // No slot found in existing rows; start a new row from the left corner.
  return { x: 0, y: maxY };
}

function mergePayload(current: unknown, patch: unknown): unknown {
  if (
    typeof current === 'object' &&
    current !== null &&
    typeof patch === 'object' &&
    patch !== null
  ) {
    return { ...current, ...(patch as object) };
  }
  return patch;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function payloadsEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) {
    return true;
  }

  if (Array.isArray(left) && Array.isArray(right)) {
    return (
      left.length === right.length && left.every((item, index) => payloadsEqual(item, right[index]))
    );
  }

  if (isPlainObject(left) && isPlainObject(right)) {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) {
      return false;
    }
    return leftKeys.every((key) => payloadsEqual(left[key], right[key]));
  }

  return false;
}

function shouldReplacePanel(current: PanelDescriptor, next: PanelDescriptor): boolean {
  return next.updatedAt >= current.updatedAt;
}

type PanelState = {
  panels: PanelDescriptor[];
  /**
   * Panel id of the most recently focused/interacted panel. Used by global
   * keyboard shortcuts (close/maximize) so power users can drive panels
   * without clicking the panel chrome first.
   */
  focusedPanelId: string | null;
  openSurface: (
    key: string,
    payload?: unknown,
    opts?: {
      correlationId?: string;
      title?: string;
      layout?: { x: number; y: number; w: number; h: number };
      enterDelayMs?: number;
    },
  ) => string;
  closePanel: (id: string) => void;
  replaceWorkspacePanels: (panels: PanelDescriptor[]) => void;
  setPanelLayout: (
    id: string,
    layout: { x: number; y: number; w: number; h: number },
    updatedAt?: number,
    cols?: number,
  ) => void;
  patchPanelPayload: (id: string, patch: unknown, updatedAt?: number) => void;
  detachPanel: (id: string, updatedAt?: number) => PanelDescriptor | null;
  restorePanel: (id: string, updatedAt?: number) => void;
  replacePanel: (panel: PanelDescriptor) => void;
  closeAllPanels: () => void;
  togglePinPanel: (id: string) => void;
  renamePanelTitle: (id: string, title: string) => void;
  setFocusedPanel: (id: string | null) => void;
  handleUiAction: (action: AiWorkbenchAction, correlationId?: string) => void;
};

export const usePanelStore = create<PanelState>((set, get) => ({
  panels: [],
  focusedPanelId: null,

  openSurface: (key, payload, opts) => {
    const cols = getWorkspaceGridCols();
    const entry = resolveSurface(key);
    const panelId = generatePanelId();
    const existingPanels = get().panels;
    const defaultLayout = entry?.defaultLayout ?? { w: 6, h: 8 };
    const normalizedDefault = scaleLayoutCols(
      { x: 0, y: 0, ...defaultLayout },
      BASE_GRID_COLS,
      cols,
    );
    const pos = findFirstFitPosition(
      existingPanels,
      { w: normalizedDefault.w, h: normalizedDefault.h },
      cols,
    );
    const layout = sanitizeLayout(opts?.layout ?? { ...normalizedDefault, ...pos }, cols);

    const descriptor: PanelDescriptor = {
      panelId,
      surface: key,
      kind: entry?.kind ?? 'form',
      payload: payload ?? (entry == null ? { surfaceKey: key } : {}),
      layout,
      layoutCols: cols,
      enterDelayMs: opts?.enterDelayMs,
      sourceCorrelation: opts?.correlationId,
      title: opts?.title,
      detached: false,
      updatedAt: Date.now(),
    };

    set((s) => ({ panels: [...s.panels, descriptor], focusedPanelId: panelId }));
    return panelId;
  },

  closePanel: (id) => {
    set((s) => {
      const remaining = s.panels.filter((p) => p.panelId !== id);
      const stillFocused = s.focusedPanelId === id ? null : s.focusedPanelId;
      return { panels: remaining, focusedPanelId: stillFocused };
    });
  },

  closeAllPanels: () => {
    // Pinned panels survive Close All — see issue 186.
    set((s) => {
      const survivors = s.panels.filter((p) => p.pinned);
      const nextFocus =
        s.focusedPanelId && survivors.some((p) => p.panelId === s.focusedPanelId)
          ? s.focusedPanelId
          : survivors.at(-1)?.panelId ?? null;
      return { panels: survivors, focusedPanelId: nextFocus };
    });
  },

  togglePinPanel: (id) => {
    set((s) => ({
      panels: s.panels.map((p) =>
        p.panelId === id ? { ...p, pinned: !p.pinned, updatedAt: Date.now() } : p,
      ),
    }));
  },

  renamePanelTitle: (id, title) => {
    const trimmed = title.trim();
    set((s) => ({
      panels: s.panels.map((p) =>
        p.panelId === id
          ? {
              ...p,
              customTitle: trimmed === '' ? undefined : trimmed,
              updatedAt: Date.now(),
            }
          : p,
      ),
    }));
  },

  setFocusedPanel: (id) => {
    set((state) => (state.focusedPanelId === id ? state : { focusedPanelId: id }));
  },

  replaceWorkspacePanels: (panels) => {
    set((state) => {
      const detachedPanels = state.panels.filter((panel) => panel.detached);
      return {
        panels: [...panels, ...detachedPanels],
        focusedPanelId: panels.at(-1)?.panelId ?? state.focusedPanelId,
      };
    });
  },

  setPanelLayout: (id, layout, updatedAt, cols = getWorkspaceGridCols()) => {
    const nextUpdatedAt = updatedAt ?? Date.now();
    const nextLayout = sanitizeLayout(layout, cols);
    set((s) => ({
      panels: s.panels.map((p) =>
        p.panelId === id
          ? p.layout.x === nextLayout.x &&
            p.layout.y === nextLayout.y &&
            p.layout.w === nextLayout.w &&
            p.layout.h === nextLayout.h
            ? p
            : {
                ...p,
                layout: nextLayout,
                layoutCols: cols,
                updatedAt: nextUpdatedAt,
              }
          : p,
      ),
    }));
  },

  patchPanelPayload: (id, patch, updatedAt) => {
    const nextUpdatedAt = updatedAt ?? Date.now();
    set((s) => ({
      panels: s.panels.map((p) =>
        p.panelId === id
          ? (() => {
              const nextPayload = mergePayload(p.payload, patch);
              if (payloadsEqual(p.payload, nextPayload)) {
                return p;
              }
              return {
                ...p,
                payload: nextPayload,
                updatedAt: nextUpdatedAt,
              };
            })()
          : p,
      ),
    }));
  },

  detachPanel: (id, updatedAt) => {
    const nextUpdatedAt = updatedAt ?? Date.now();
    let detached: PanelDescriptor | null = null;

    set((s) => ({
      panels: s.panels.map((p) => {
        if (p.panelId !== id) return p;
        detached = {
          ...p,
          detached: true,
          updatedAt: nextUpdatedAt,
        };
        return detached;
      }),
    }));

    return detached;
  },

  restorePanel: (id, updatedAt) => {
    const nextUpdatedAt = updatedAt ?? Date.now();
    set((s) => ({
      panels: s.panels.map((p) =>
        p.panelId === id
          ? {
              ...p,
              detached: false,
              updatedAt: Math.max(p.updatedAt, nextUpdatedAt),
            }
          : p,
      ),
    }));
  },

  replacePanel: (panel) => {
    set((s) => {
      const current = s.panels.find((existing) => existing.panelId === panel.panelId);
      if (!current) {
        return { panels: [...s.panels, panel] };
      }

      if (!shouldReplacePanel(current, panel)) {
        return { panels: s.panels };
      }

      return {
        panels: s.panels.map((existing) => (existing.panelId === panel.panelId ? panel : existing)),
      };
    });
  },

  handleUiAction: (action, correlationId) => {
    const { type, surface, title, payload } = action;

    switch (type) {
      case 'open_form':
      case 'open_report':
      case 'open_detail': {
        if (!surface) {
          console.warn('[panelStore] ui_action missing surface key', action);
          return;
        }
        const entry = resolveSurface(surface);
        if (!entry) {
          // Unknown surface: log telemetry, open fallback

          console.warn('[panelStore] unknown surface, rendering fallback', surface);
          get().openSurface(surface, { surfaceKey: surface }, { correlationId, title });
          return;
        }
        get().openSurface(surface, payload ?? {}, { correlationId, title });
        break;
      }

      case 'update_panel': {
        // Patch the most recently opened panel for this surface
        const panels = get().panels;
        const target = [...panels].reverse().find((p) => p.surface === surface);
        if (target) {
          get().patchPanelPayload(target.panelId, payload ?? {});
        }
        break;
      }

      case 'stack_panels': {
        const items = Array.isArray(action.payload?.['surfaces'])
          ? (action.payload['surfaces'] as Array<{
              surface: string;
              payload?: unknown;
              title?: string;
            }>)
          : [];
        if (items.length === 0) {
          break;
        }

        const baseY = maxBottom(workspacePanels(get().panels));
        const cols = getWorkspaceGridCols();
        let cursorX = 0;
        let cursorY = baseY;
        let rowHeight = 0;

        for (let index = 0; index < items.length; index += 1) {
          const item = items[index]!;
          const entry = resolveSurface(item.surface);
          const defaultLayout = entry?.defaultLayout ?? { w: 6, h: 8 };
          const defaultGridLayout = scaleLayoutCols(
            { x: 0, y: 0, ...defaultLayout },
            BASE_GRID_COLS,
            cols,
          );
          const width = defaultGridLayout.w;
          const height = defaultGridLayout.h;

          if (cursorX + width > cols) {
            cursorX = 0;
            cursorY += rowHeight;
            rowHeight = 0;
          }

          get().openSurface(item.surface, item.payload, {
            correlationId,
            title: item.title,
            layout: {
              x: cursorX,
              y: cursorY,
              w: width,
              h: height,
            },
            enterDelayMs: index * 80,
          });

          cursorX += width;
          rowHeight = Math.max(rowHeight, height);
        }
        break;
      }

      case 'show_suggestions':
      case 'save_template':
      case 'no_op':
        // Handled in chat layer; nothing to do for panel store
        break;

      default:
        console.warn('[panelStore] unknown ui_action type', type);
    }
  },
}));
